/**
 * Typed client for the `/api/runs*` endpoints.
 *
 * Mirrors `apps/api/src/runs.ts` types intentionally — the web app avoids
 * importing server code so the two sides can version independently.
 */

export type RunStatus = "running" | "done" | "failed" | "cancelled";

export interface RunOptions {
  url: string;
  maxPages?: number;
  emit?: Array<
    | "selenium-java"
    | "selenium-python"
    | "playwright-ts"
    | "playwright-java"
    | "playwright-python"
    | "cypress-ts"
    | "uipath"
  >;
  semantic?: boolean;
  authPath?: string;
  /** Plan §22 Login UX. Vault-saved auth state id; takes precedence
   *  over `authPath` when both are set. */
  authStateId?: string;
  hud?: boolean;
  /**
   * Required for interactive starts: the caller affirms they have permission
   * to crawl the target site. The server rejects with 400 PERMISSION_NOT_AFFIRMED
   * when this is not literal `true`. Scheduler ticks bypass this gate (the
   * affirmation was captured at schedule-creation time).
   */
  permissionAffirmed?: boolean;
}

export interface LogLine {
  at: string;
  stream: "stdout" | "stderr" | "system";
  line: string;
}

export interface RunSummary {
  id: string;
  url: string;
  outDir: string;
  cmd: string;
  args: string[];
  status: RunStatus;
  exitCode: number | null;
  startedAt: string;
  finishedAt: string | null;
  logCount: number;
  options: RunOptions;
}

/**
 * Error thrown when POST /api/runs is rejected by the plan gate.
 *
 * The server returns a stable `code` (`ANON_DAILY_LIMIT`, `MONTHLY_LIMIT`,
 * `HOST_NOT_ALLOWED`, `AUTH_REQUIRED`) + user-facing `message`. The UI
 * branches on `code`/`status` to show the right upsell CTA.
 */
export class RunStartError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "RunStartError";
    this.status = status;
    this.code = code;
  }
}

export async function startRun(opts: RunOptions): Promise<RunSummary> {
  const res = await fetch("/api/runs", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) {
    const message =
      (typeof body?.error === "string" && body.error) ||
      `failed to start run (${res.status} ${res.statusText})`;
    const code = typeof body?.code === "string" ? body.code : null;
    throw new RunStartError(message, res.status, code);
  }
  return body.run as RunSummary;
}

export async function cancelRun(id: string): Promise<void> {
  const res = await fetch(`/api/runs/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`cancel failed: ${res.status}`);
}

export interface RunStreamHandlers {
  onHello?: (hello: { runId: string; status: RunStatus; outDir: string }) => void;
  onLog?: (line: LogLine) => void;
  onStatus?: (status: RunStatus) => void;
  onDone?: (done: {
    runId: string;
    status: RunStatus;
    exitCode: number | null;
    outDir: string;
  }) => void;
  onError?: (err: Error) => void;
}

/**
 * Subscribe to the SSE event stream for a run. Returns a teardown fn that
 * closes the underlying EventSource. Safe to call teardown multiple times.
 */
export function subscribeToRun(
  id: string,
  handlers: RunStreamHandlers,
): () => void {
  const es = new EventSource(`/api/runs/${encodeURIComponent(id)}/events`);

  es.addEventListener("hello", (ev: MessageEvent) => {
    try {
      handlers.onHello?.(JSON.parse(ev.data));
    } catch {
      /* ignore malformed event */
    }
  });
  es.addEventListener("log", (ev: MessageEvent) => {
    try {
      handlers.onLog?.(JSON.parse(ev.data));
    } catch {
      /* ignore */
    }
  });
  es.addEventListener("status", (ev: MessageEvent) => {
    try {
      const { status } = JSON.parse(ev.data);
      handlers.onStatus?.(status);
    } catch {
      /* ignore */
    }
  });
  es.addEventListener("done", (ev: MessageEvent) => {
    try {
      handlers.onDone?.(JSON.parse(ev.data));
    } catch {
      /* ignore */
    }
    es.close();
  });
  es.onerror = () => {
    // The browser's EventSource fires onerror on normal close too; only
    // surface an error if the stream was never established.
    if (es.readyState === EventSource.CLOSED) return;
    handlers.onError?.(new Error("SSE connection error"));
  };

  return () => {
    try {
      es.close();
    } catch {
      /* ignore */
    }
  };
}
